/**
 * Conectores de un cargador agrupados por estándar, para mostrarlos con su ícono de marca. Misma
 * regla que `connectorSummary` (format.ts): `2 × CCS2 + CCS1 · hasta 180 kW · compartida entre
 * conectores` cuando se conoce el gabinete; si no, la potencia mayor por conector.
 */
import {
  type ConnectorLike,
  connectorStandard,
  type Locale,
  powerKw,
  powerSummary,
} from './format.ts';

export interface ConnectorGroup {
  /** Estándar OCPI del primer conector del grupo (para elegir el ícono). */
  standard: string;
  /** Nombre comercial en el idioma pedido. */
  label: string;
  count: number;
}

export interface ConnectorGroups {
  groups: ConnectorGroup[];
  /** Potencia en palabras, o `null` si no se conoce. */
  power: string | null;
}

export function connectorGroups(
  connectors: readonly ConnectorLike[] | null | undefined,
  locale: Locale = 'es',
): ConnectorGroups {
  if (!connectors || connectors.length === 0) return { groups: [], power: null };
  const groups: ConnectorGroup[] = [];
  for (const connector of connectors) {
    const label = connectorStandard(connector.standard, locale);
    const group = groups.find((g) => g.label === label);
    if (group) group.count += 1;
    else groups.push({ standard: connector.standard, label, count: 1 });
  }
  const cabinet = connectors.find((c) => c.charger_max_power_w != null)?.charger_max_power_w;
  if (cabinet) return { groups, power: powerSummary(cabinet, connectors, locale) };
  const maxW = Math.max(...connectors.map((c) => c.max_power_w ?? 0));
  return { groups, power: maxW > 0 ? powerKw(maxW, locale) : null };
}

/** El resumen en texto plano (`2 × CCS2 + CCS1 · 180 kW`), idéntico a `connectorSummary`. */
export function connectorGroupsText({ groups, power }: ConnectorGroups): string {
  if (groups.length === 0) return '—';
  const parts = groups.map((g) => (g.count > 1 ? `${g.count} × ${g.label}` : g.label));
  return `${parts.join(' + ')}${power ? ` · ${power}` : ''}`;
}
